import {
  select,
  scaleBand,
  scaleLinear,
  axisLeft,
  axisRight,
  axisBottom,
  timeFormat,
  range,
  scaleTime,
  selectAll,
  line as lineD3,
  curveLinear,
  min,
  curveMonotoneX,
  area as areaD3,
  pie,
  arc as arcD3,
  max,
  interpolateRgb,
  geoPath,
  zip,
  symbolTriangle,
  symbolCircle,
  symbolCross,
  symbolDiamond,
  symbolSquare,
  symbolStar,
  symbolWye,
  contours as contoursD3,
  symbol,
  format as formatD3,
  event,
} from "d3";
import { qVolatility } from "@/_core/_helpers/_quadraticVolatility";
import {
  dateInterpolate,
  vLineCreate,
} from "@/_core/charts/D3ChartHoverHelpers";
import {
  derivativesTooltipTxt,
  ratesSmTooltip,
} from "@/_core/charts/D3ChartAnalyticsChartHelpers";
import {
  derivativeImage,
  derivativeImageHighlight,
} from "@/_core/charts/D3ChartAnalyticsChartHelpers";
import {
  backgroundGrad1,
  backgroundGrad2,
  colorCall,
  colorCallAlpha,
  colorDarkExpo,
  colorFwd,
  colorFwdAlpha,
  colorPut,
  colorPutAlpha,
  darkBackground4,
  darkCall,
  darkCallAlpha,
  darkFwd,
  darkFwdAlpha,
  darkPut,
  darkPutAlpha,
  textactive,
  textactive2,
} from "@/_core/_helpers/_parametric_tools";
import { definedFct } from "@/_core/_helpers/_helpers";

// para pintar lineas en el gráfico
// https://blocks.roadtolarissa.com/nitaku/d79632a53187f8e92b15
// https://observablehq.com/@d3/draw-me
// https://blocks.roadtolarissa.com/jakosz/d1fe172445cd753b7d5c
// zoom https://blocks.roadtolarissa.com/jgbos/9752277
// brush and zoom
// https://blocks.roadtolarissa.com/mbostock/34f08d5e11952a80609169b7917d4172
// https://observablehq.com/@d3/focus-context

// Multiline Pan & Zoom with voronoi mouseover:
// https://blocks.roadtolarissa.com/lorenzopub/6a56e17551d59278631dffd7f65a0cda

// doble click
// https://stackoverflow.com/questions/20031254/how-can-i-make-double-click-event-on-node-in-d3-js

// añadir quitar datos
// https://blocks.roadtolarissa.com/alansmithy/e984477a741bc56db5a5

// Brush and zoom
// https://blocks.roadtolarissa.com/EfratVil/92f894ac0ba265192411e73f633a3e2f

// zoom https://blocks.roadtolarissa.com/tompiler/6045b80d2164077faaf96e0304531bba

// http://www.d3noob.org/2015/07/clipped-paths-in-d3js-aka-clippath.html
// http://jsfiddle.net/ashr/TSgSs/1/ http://jsfiddle.net/dmitrynizh/3fca7s1c/
// https://codepen.io/parliament718/pen/BaNQPXx
// para poner view del mercado https://bl.ocks.org/1wheel/07d9040c3422dac16bd5be741433ff1e
// para lineas de tendencia https://bl.ocks.org/denisemauldin/538bfab8378ac9c3a32187b4d7aed2c2

/**
 * Create "class" instance
 * @param {string} _parentElement: id of div containing SVG
 * @param {string} _childElement: id of svg
 */
export const D3Chart = function(_parentElement, _childElement) {
  this.parentElement = _parentElement;
  this.childElement = _childElement;
};

D3Chart.prototype.setSVG = function(
  topPerc,
  topPx,
  rightPerc,
  rightPx,
  bottomPerc,
  bottomPx,
  leftPerc,
  leftPx,
  colorTheme = true,
  logo = null,
  logoPos = "up",
  isSmall = false,
) {
  // set the dimensions and margins of the graph
  var chartDiv = document.getElementById(this.parentElement);
  // Extract the width and height that was computed by CSS.
  var width;
  if (chartDiv == null) {
    return { height: 0, width: 0 };
  }
  if ("clientWidth" in chartDiv) {
    width = chartDiv.clientWidth;
  } else {
    return { height: 0, width: 0 };
  }
  var height = chartDiv.clientHeight;
  var margin = {
    top: Math.min(height * topPerc, topPx),
    right: Math.min(width * rightPerc, rightPx),
    bottom: Math.min(height * bottomPerc, bottomPx),
    left: Math.min(width * leftPerc, leftPx),
  };
  width = width - margin.left - margin.right;
  height = height - margin.top - margin.bottom;

  select("#" + this.childElement)
    .select("svg > *")
    .remove();

  const svg = select("#" + this.childElement)
    .attr("width", width + margin.left + margin.right)
    .attr("height", height + margin.top + margin.bottom)
    .style("fill", "rgba(0, 0, 0, 0)")
    .append("g")
    .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

  svg.parentElement = this.parentElement;
  svg.margin = margin;
  svg.width = width;
  svg.height = height;

  if (!colorTheme) {
    const grad = svg
      .append("defs")
      .append("linearGradient")
      .attr("id", "grad")
      .attr("x1", "100%")
      .attr("x2", "100%")
      .attr("y1", "0%")
      .attr("y2", "100%");

    const colors = [backgroundGrad1(colorTheme), backgroundGrad2(colorTheme)];
    grad
      .selectAll("stop")
      .data(colors)
      .enter()
      .append("stop")
      .style("stop-color", function(d) {
        return d;
      })
      .attr("offset", function(d, i) {
        return 100 * (i / (colors.length - 1)) + "%";
      });

    svg
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", width)
      .attr("height", height)
      .attr("fill-opacity", 1)
      .style("fill", "url(#grad)");
  }

  if (logo != null) {
    let logoSize = isSmall ? 30 : 50;
    let offst = isSmall ? 10 : 20;
    let yPos = logoPos === "up" ? offst : svg.height - logoSize - offst;
    let xPos = logoSize + offst;
    svg
      .append("image")
      .attr("xlink:href", logo)
      .attr("x", svg.width - xPos + "px")
      .attr("y", yPos)
      .attr("width", logoSize + "px")
      .attr("height", logoSize + "px")
      .style("opacity", 0.3);
  }

  return svg;
};

D3Chart.prototype.setPieSVG = function(
  topPerc,
  topPx,
  rightPerc,
  rightPx,
  bottomPerc,
  bottomPx,
  leftPerc,
  leftPx,
  remove = true,
) {
  // set the dimensions and margins of the graph
  var chartDiv = document.getElementById(this.parentElement);
  // Extract the width and height that was computed by CSS.
  var width;
  if (chartDiv == null) {
    return { height: 0, width: 0 };
  }
  if ("clientWidth" in chartDiv) {
    width = chartDiv.clientWidth;
  } else {
    return { height: 0, width: 0 };
  }
  var height = chartDiv.clientHeight;
  var margin = {
    top: Math.min(height * topPerc, topPx),
    right: Math.min(width * rightPerc, rightPx),
    bottom: Math.min(height * bottomPerc, bottomPx),
    left: Math.min(width * leftPerc, leftPx),
  };
  width = width - margin.left - margin.right;
  height = height - margin.top - margin.bottom;

  if (remove === true) {
    select("#" + this.childElement)
      .select("svg > *")
      .remove();
  }
  const svg = select("#" + this.childElement)
    .attr("width", width + margin.left + margin.right)
    .attr("height", height + margin.top + margin.bottom)
    .style("fill", "rgba(0, 0, 0, 0)")
    .append("g")
    .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")");

  svg.parentElement = this.parentElement;
  svg.margin = margin;
  svg.width = width;
  svg.height = height;

  return svg;
};

D3Chart.prototype.setAxis = function(
  svg,
  xData,
  minMaxYAxis,
  xFormat,
  nXTicks = 60,
  nYTicks = 8,
  customTicks = false,
  tickLabels,
  addXFormat = true,
  showY = true,
  addYFormat = false,
  yFormat,
  yAxisLeft = true,
  showX = true,
  xIsLinear = false,
  minMaxXAxis = [0, 1],
  isNice = true,
  zeroXLine = true,
) {
  var minimal = minMaxYAxis[0]; // y axis min value
  var maximal = minMaxYAxis[1]; // y axis max value

  if (xIsLinear === false) {
    svg.x = scaleBand()
      .range([0, svg.width])
      .domain(
        xData.map(function(d) {
          return d.date;
        }),
      );
  } else {
    if (isNice) {
      svg.x = scaleLinear()
        .range([0, svg.width])
        .domain([minMaxXAxis[0], minMaxXAxis[1]])
        .nice();
    } else {
      svg.x = scaleLinear()
        .range([0, svg.width])
        .domain([minMaxXAxis[0], minMaxXAxis[1]]);
    }
  }

  if (isNice) {
    svg.y = scaleLinear()
      .range([svg.height, 0])
      .domain([minimal, maximal])
      .nice();
  } else {
    svg.y = scaleLinear()
      .range([svg.height, 0])
      .domain([minimal, maximal]);
  }

  let xAxisAux, widthAux;
  if (yAxisLeft === true) {
    xAxisAux = axisLeft(svg.y);
    widthAux = 0;
  } else {
    xAxisAux = axisRight(svg.y);
    widthAux = svg.width;
  }

  if (showY === true) {
    // add the y Axis
    if (customTicks === true) {
      if (addYFormat === true) {
        svg.yAxis = xAxisAux
          .tickSize(-svg.width)
          .tickSizeOuter(0)
          .tickPadding(10)
          .ticks(nYTicks)
          .tickValues(tickLabels)
          .tickFormat(yFormat);
      } else {
        svg.yAxis = xAxisAux
          .tickSize(-svg.width)
          .tickSizeOuter(0)
          .tickPadding(10)
          .ticks(nYTicks)
          .tickValues(tickLabels);
      }
      svg.gY = svg
        .append("g")
        .attr("class", "axis axis--y")
        .attr("transform", "translate( " + widthAux + ", 0 )")
        .call(svg.yAxis);
    } else {
      if (addYFormat === true) {
        svg.yAxis = xAxisAux
          .tickSize(-svg.width)
          .tickSizeOuter(0)
          .tickPadding(10)
          .ticks(nYTicks)
          .tickFormat(yFormat);
      } else {
        svg.yAxis = xAxisAux
          .tickSize(-svg.width)
          .tickSizeOuter(0)
          .tickPadding(10)
          .ticks(nYTicks);
      }
      svg.gY = svg
        .append("g")
        .attr("class", "axis axis--y")
        .attr("transform", "translate( " + widthAux + ", 0 )")
        .call(svg.yAxis);
    }
  }

  // add the x Axis
  if (xIsLinear === false) {
    var xtickdelta = Math.ceil(nXTicks / (svg.width / xData.length));
    if (showX) {
      if (addXFormat === true) {
        svg
          .append("g")
          .attr("class", "axis axis--x")
          .attr("transform", "translate(0," + svg.height + ")")
          .call(
            axisBottom(svg.x)
              .tickValues(
                svg.x.domain().filter(function(d, i) {
                  return !((i + Math.floor(xtickdelta / 2)) % xtickdelta);
                }),
              )
              .tickFormat(timeFormat(xFormat)),
          );
      } else {
        svg
          .append("g")
          .attr("class", "axis axis--x")
          .attr("transform", "translate(0," + svg.height + ")")
          .call(
            axisBottom(svg.x).tickValues(
              svg.x.domain().filter(function(d, i) {
                return !((i + Math.floor(xtickdelta / 2)) % xtickdelta);
              }),
            ),
          );
      }
    }
  } else {
    svg.xAxis = axisBottom(svg.x)
      .tickSize(-svg.height)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(nYTicks);
    svg
      .append("g")
      .attr("class", "axis axis--x")
      .attr("transform", "translate(0," + svg.height + ")")
      .call(svg.xAxis);
  }

  // add a horizontal line at the max value of the yaxis
  svg
    .append("line")
    .attr("class", "xAxisLine")
    .attr("x1", 0)
    .attr("y1", 0)
    .attr("x2", svg.width)
    .attr("y2", 0);

  if (
    Number.isFinite(minimal) &&
    Number.isFinite(maximal) &&
    Math.sign(minimal * maximal) < 0 &&
    zeroXLine
  ) {
    // add a horizontal line at y = 0
    svg.xAxisLine = svg
      .append("line")
      .attr("class", "xAxisLine")
      .attr("x1", svg.x(0))
      .attr("y1", svg.y(0))
      .attr("x2", svg.width)
      .attr("y2", svg.y(0));
  }
  const gYWidth = svg.width + svg.margin.right + svg.margin.left;

  // Determine the x-position based on yAxisLeft
  const xPosition = yAxisLeft ? svg.width : gYWidth;

  // Create the rectangle
  svg.gYRect = svg
    .append("g")
    .attr("class", "axis axis--y--rect")
    .attr("transform", `translate(${xPosition}, 0)`)
    .append("rect")
    .attr("x", -gYWidth)
    .attr("y", 0)
    .attr("width", gYWidth)
    .attr("height", svg.height)
    .attr("fill-opacity", 0);
  return svg;
};

D3Chart.prototype.setAxisDateLin = function(
  svg,
  xRange,
  minMaxYAxis,
  yAxisLeft = true,
  zeroXLine = true,
  customYTicks = false,
  yTickLabels,
  zeroLineField = "zeroLine",
  svgAux = null,
  showTickSize = true,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  svg.x = scaleTime().range(range(0, svg.width, svg.width / xRange.length));
  svg.y = scaleLinear().range([svg.height, 0]);

  svg.x.domain(xRange);
  svg.y.domain(minMaxYAxis);

  let tickCount = svg.width / 100;
  svg.xAxis = axisBottom(svg.x)
    .ticks(((svg.width + 2) / (svg.height + 2)) * 5)
    .tickSize(showTickSize ? -svg.height : 0)
    .tickPadding(10)
    .ticks(tickCount);

  let widthAux;
  tickCount = svg.height / 40;
  if (yAxisLeft === true) {
    svg.yAxis = axisLeft(svg.y)
      .tickSize(showTickSize ? -svg.width : 0)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(tickCount);
    widthAux = 0;
  } else {
    svg.yAxis = axisRight(svg.y)
      .tickSize(showTickSize ? -svg.width : 0)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(tickCount);
    widthAux = svg.width;
  }
  if (customYTicks === true) {
    svg.yAxis.tickValues(yTickLabels);
  }

  svg.gX = svg
    .append("g")
    .attr("class", "axis axis--x")
    .attr("transform", "translate(0," + svg.height + ")")
    .call(svg.xAxis);
  svg.gY = svg
    .append("g")
    .attr("class", "axis axis--y")
    .attr("transform", "translate( " + widthAux + ", 0 )")
    .call(svg.yAxis);

  const gYWidth = svg.width + svg.margin.right + svg.margin.left;

  svg.gYRect = svg
    .append("g")
    .attr("class", "axis axis--y--rect")
    .attr("transform", "translate( " + gYWidth + ", 0 )")
    .append("rect")
    .attr("x", -gYWidth)
    .attr("y", 0)
    .attr("width", gYWidth)
    .attr("height", svg.height)
    .attr("fill-opacity", 0);

  // add a horizontal line at the max value of the yaxis
  svg
    .append("line")
    .attr("class", "xAxisLine")
    .attr("x1", 0)
    .attr("y1", 0)
    .attr("x2", svg.width)
    .attr("y2", 0);

  if (zeroXLine) {
    // add a horizontal line at y = 0
    svg[zeroLineField] = svgAux
      .append("line")
      .attr("class", "xAxisLine")
      .attr("x1", 0)
      .attr("y1", svg.y(0))
      .attr("x2", svg.width)
      .attr("y2", svg.y(0));
  }
  return svg;
};

D3Chart.prototype.setDoubleAxisDateLin = function(
  svg,
  xRange,
  minMaxYAxis,
  minMax2YAxis,
  color1,
  color2,
  invert2 = false,
) {
  svg.x = scaleTime().range(range(0, svg.width, svg.width / xRange.length));
  svg.y = scaleLinear().range([svg.height, 0]);
  svg.y2 = scaleLinear().range([
    invert2 ? 0 : svg.height,
    invert2 ? svg.height : 0,
  ]);

  svg.x.domain(xRange);
  svg.y.domain(minMaxYAxis);
  svg.y2.domain(minMax2YAxis);

  let tickCount = svg.width / 100;
  svg.xAxis = axisBottom(svg.x)
    .ticks(((svg.width + 2) / (svg.height + 2)) * 5)
    .tickSize(-svg.height)
    .tickPadding(10)
    .ticks(tickCount);

  tickCount = svg.height / 40;
  svg.yAxis = axisLeft(svg.y)
    .tickSize(-svg.width)
    .tickSizeOuter(0)
    .tickPadding(10)
    .ticks(tickCount);
  svg.y2Axis = axisRight(svg.y2)
    .tickSize(0)
    .tickSizeOuter(0)
    .tickPadding(10)
    .ticks(tickCount);

  svg.gX = svg
    .append("g")
    .attr("class", "axis axis--x")
    .attr("transform", "translate(0," + svg.height + ")")
    .call(svg.xAxis);
  svg.gY = svg
    .append("g")
    .attr("class", "axis axis--y y-axis-1")
    .attr("transform", "translate( 0, 0 )")
    .call(svg.yAxis);
  selectAll(".y-axis-1 path").style("stroke", color1);
  selectAll(".y-axis-1 text").style("fill", color1);

  svg.gY2 = svg
    .append("g")
    .attr("class", "axis axis--y y-axis-2")
    .attr("transform", "translate( " + svg.width + ", 0 )")
    .call(svg.y2Axis);
  selectAll(".y-axis-2 path").style("stroke", color2);
  selectAll(".y-axis-2 text").style("fill", color2);

  // add a horizontal line at the max value of the yaxis
  // svg
  //   .append("line")
  //   .attr("class", "xAxisLine")
  //   .attr("x1", 0)
  //   .attr("y1", 0)
  //   .attr("x2", svg.width)
  //   .attr("y2", 0);

  return svg;
};

D3Chart.prototype.setAxisLinBand = function(
  svg,
  yData,
  yNames,
  showY = true,
  minMaxXAxis,
  nXTicks = 6,
  addXFormat = false,
  xFormat,
  fontSize = 0.7,
  yCrossAtZero = true,
  tickSize = 0,
) {
  var minimal = minMaxXAxis[0]; // y axis min value
  var maximal = minMaxXAxis[1]; // y axis max value

  svg.x = scaleLinear()
    .range([0, svg.width])
    .domain([minimal, maximal])
    .nice();

  svg.y = scaleBand()
    .range([svg.height, 0])
    .domain(yData);

  if (showY === true) {
    svg.yAxis = axisLeft(svg.y)
      .tickSize(tickSize)
      .tickSizeOuter(0)
      .tickPadding(10)
      .tickValues(yData)
      .tickFormat((_, i) => yNames[i]);
    let cross;
    if (yCrossAtZero === true) {
      cross = svg.x(0);
    } else {
      cross = 0;
    }
    svg
      .append("g")
      .attr("class", "axis axis--y")
      .attr("transform", "translate( " + cross + ", 0 )")
      .style("font-size", fontSize + "rem")
      .call(svg.yAxis);
  }

  if (addXFormat === true) {
    svg.xAxis = axisBottom(svg.x)
      .ticks(nXTicks)
      .tickFormat(xFormat);
  } else {
    svg.xAxis = axisBottom(svg.x).ticks(nXTicks);
  }
  svg
    .append("g")
    .attr("class", "axis axis--x")
    .attr("transform", "translate(0," + svg.height + ")")
    .call(svg.xAxis);

  return svg;
};

D3Chart.prototype.setBandAxis = function(
  svg,
  xData,
  yData,
  xNames,
  yNames,
  showX = true,
  showY = true,
) {
  svg.x = scaleBand()
    .range([0, svg.width])
    .domain(xData);
  svg.y = scaleBand()
    .range([svg.height, 0])
    .domain(yData);
  if (showY === true) {
    svg.yAxis = axisLeft(svg.y)
      .tickSize(0)
      .tickSizeOuter(0)
      .tickPadding(10)
      .tickValues(yData)
      .tickFormat((_, i) => yNames[i]);
    svg
      .append("g")
      .attr("class", "axis axis--y")
      .attr("transform", "translate( 0, 0 )")
      .call(svg.yAxis);
  }
  if (showX === true) {
    svg.xAxis = axisBottom(svg.x)
      .tickSize(0)
      .tickSizeOuter(0)
      .tickValues(xData)
      .tickFormat((_, i) => xNames[i]);
    svg
      .append("g")
      .attr("class", "axis axis--x")
      .attr("transform", "translate(0," + svg.height + ")")
      .call(svg.xAxis);
  }
  return svg;
};

D3Chart.prototype.setDoubleAxis = function(
  svg,
  xData,
  minMaxYAxis,
  minMax2YAxis,
  xFormat,
  nXTicks = 60,
  nYTicks = 8,
  yFormat,
  yFormat2,
  color1,
  color2,
  invert2 = false,
  showX = true,
) {
  var minimal1 = minMaxYAxis[0]; // y axis min value
  var maximal1 = minMaxYAxis[1]; // y axis max value
  var minimal2 = minMax2YAxis[0]; // y axis min value
  var maximal2 = minMax2YAxis[1]; // y axis max value

  svg.x = scaleBand()
    .range([0, svg.width])
    .domain(
      xData.map(function(d) {
        return d.date;
      }),
    );

  svg.y = scaleLinear()
    .range([svg.height, 0])
    .domain([minimal1, maximal1])
    .nice();

  svg.y2 = scaleLinear()
    .range([invert2 ? 0 : svg.height, invert2 ? svg.height : 0])
    .domain([minimal2, maximal2])
    .nice();

  if (yFormat !== null) {
    svg.yAxis = axisLeft(svg.y)
      .tickSize(-svg.width)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(nYTicks)
      .tickFormat(yFormat);
  } else {
    svg.yAxis = axisLeft(svg.y)
      .tickSize(-svg.width)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(nYTicks);
  }

  if (yFormat2 !== null) {
    svg.y2Axis = axisRight(svg.y2)
      .tickSize(0)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(nYTicks)
      .tickFormat(yFormat2);
  } else {
    svg.y2Axis = axisRight(svg.y2)
      .tickSize(0)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(nYTicks);
  }

  svg
    .append("g")
    .attr("class", "axis axis--y y-axis-1")
    .attr("transform", "translate( 0, 0 )")
    .call(svg.yAxis);

  selectAll(".y-axis-1 path").style("stroke", color1);
  selectAll(".y-axis-1 text").style("fill", color1);

  svg
    .append("g")
    .attr("class", "axis axis--y y-axis-2")
    .attr("transform", "translate( " + svg.width + ", 0 )")
    .call(svg.y2Axis);

  selectAll(".y-axis-2 path").style("stroke", color2);
  selectAll(".y-axis-2 text").style("fill", color2);

  // add the x Axis
  if (showX) {
    var xtickdelta = Math.ceil(nXTicks / (svg.width / xData.length));
    svg
      .append("g")
      .attr("class", "axis axis--x")
      .attr("transform", "translate(0," + svg.height + ")")
      .call(
        axisBottom(svg.x)
          .tickValues(
            svg.x.domain().filter(function(d, i) {
              return !((i + Math.floor(xtickdelta / 2)) % xtickdelta);
            }),
          )
          .tickFormat(timeFormat(xFormat)),
      );

    // add a horizontal line at the max value of the yaxis
    svg
      .append("line")
      .attr("class", "xAxisLine")
      .attr("x1", svg.x(0))
      .attr("y1", 0)
      .attr("x2", svg.width)
      .attr("y2", 0);

    if (Math.sign(minimal1 * maximal1) < 0) {
      // add a horizontal line at y = 0
      svg
        .append("line")
        .attr("class", "xAxisLine")
        .attr("x1", svg.x(0))
        .attr("y1", svg.y(0))
        .attr("x2", svg.width)
        .attr("y2", svg.y(0));
    }
  }
  return svg;
};

D3Chart.prototype.scatter = function(
  svg,
  data,
  xField = "rates",
  yField = "rates",
  fillColor = textactive,
  fillOpacity = 1,
  strokeOpacity = 1,
  lineWidth = 1,
  radius = 3,
) {
  svg
    .append("g")
    .selectAll("dot")
    .data(data)
    .enter()
    .append("circle")
    .attr("cx", function(d) {
      return svg.x(d[xField]);
    })
    .attr("cy", function(d) {
      return svg.y(d[yField]);
    })
    .attr("r", radius)
    .style("fill", fillColor)
    .attr("fill-opacity", fillOpacity)
    .style("stroke", fillColor)
    .style("stroke-opacity", strokeOpacity)
    .attr("stroke-width", lineWidth)
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");
};

D3Chart.prototype.candles = function(
  svg,
  data,
  upColor,
  dnColor,
  customName,
  opacityUp = 1,
  opacityDn = 1,
  upColorBackground = null,
  dnColorBackground = null,
  lineWidth = 1,
  customX = false,
  deltaBand = 1 / 22,
  xRange = [],
  addId = false,
  idField = "id",
  xIsDate = false,
  fieldStick1 = "stick",
  fieldStick2 = "stick2",
  svgAux = null,
  bandWidthAux = 0,
  offsetBandWidth = 0,
  oldChart = true,
  barWidth = 0.7,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  if (upColorBackground === null) {
    upColorBackground = upColor;
  }
  if (dnColorBackground === null) {
    dnColorBackground = dnColor;
  }
  let bandWidth;
  if (xIsDate === false) {
    bandWidth = svg.x.bandwidth();
  } else {
    bandWidth = bandWidthAux;
  }
  const candleWidth = bandWidth * barWidth + 1;
  const delta = (bandWidth - candleWidth) / 2;
  const deltaCustom = candleWidth * deltaBand;

  if (!oldChart) {
    let series1 = data.map(function(d) {
      return {
        name: d.close >= d.open ? "up" : "dn",
        values: [{ date: d.date, y: d.high }].concat([
          { date: d.date, y: Math.max(d.open, d.close) },
        ]),
      };
    });
    let series2 = data.map(function(d) {
      return {
        name: d.close >= d.open ? "up" : "dn",
        values: [{ date: d.date, y: d.low }].concat([
          { date: d.date, y: Math.min(d.open, d.close) },
        ]),
      };
    });
    let newData = {
      name: "sticks",
      series: series1.concat(series2),
    };

    var line = lineD3()
      .curve(curveLinear) // .curve(curveMonotoneX)
      .defined((d) => d.y)
      .x((d) => svg.x(d.date) + bandWidth / 2 - offsetBandWidth)
      .y((d) => svg.y(d.y));

    svg[fieldStick1] = svgAux
      .append("g")
      .attr("class", "sticks" + customName)
      .attr("fill", "none")
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .selectAll("path")
      .data(newData.series, (d) => d.id)
      .join("path")
      .style("stroke", (d) => (d.name === "up" ? upColor : dnColor))
      .style("stroke-width", lineWidth)
      .attr("d", (d) => line(d.values))
      .attr("class", function(d, i) {
        return "stick" + customName + "" + (addId ? +d[idField] : i);
      })
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
  } else {
    svg[fieldStick1] = svgAux
      .selectAll(".sticks" + customName)
      .data([data])
      .enter()
      .append("g")
      .attr("class", "sticks" + customName);
    svg[fieldStick1]
      .selectAll("rect")
      .data(function(d) {
        return d;
      })
      .enter()
      .append("rect")
      .attr("stroke-width", lineWidth)
      .attr("x", function(d) {
        return customX
          ? dateInterpolate(xRange, d.date, svg.x, svg.x.bandwidth()) +
              deltaCustom -
              lineWidth / 2 -
              offsetBandWidth
          : svg.x(d.date) + bandWidth / 2 - offsetBandWidth;
      })
      .attr("y", function(d) {
        return svg.y(d.high);
      })
      .attr("class", function(d, i) {
        return "stick" + customName + "" + (addId ? +d[idField] : i);
      })
      .attr("height", function(d) {
        return Math.max(
          0.0000001,
          svg.y(Math.max(d.open, d.close)) - svg.y(d.high),
        );
      })
      .attr("width", 1)
      .style("fill", function(d) {
        return d.close > d.open ? upColor : dnColor;
      })
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
    // add candle shadows
    svg[fieldStick2] = svgAux
      .selectAll(".sticks2" + customName)
      .data([data])
      .enter()
      .append("g")
      .attr("class", "sticks2" + customName);
    svg[fieldStick2]
      .selectAll("rect")
      .data(function(d) {
        return d;
      })
      .enter()
      .append("rect")
      .attr("x", function(d) {
        return customX
          ? dateInterpolate(xRange, d.date, svg.x, svg.x.bandwidth()) +
              deltaCustom -
              lineWidth / 2 -
              offsetBandWidth
          : svg.x(d.date) + bandWidth / 2 - offsetBandWidth;
      })
      .attr("stroke-width", lineWidth)
      .attr("y", function(d) {
        return svg.y(Math.min(d.open, d.close));
      })
      .attr("class", function(d, i) {
        return "stick2" + customName + "" + (addId ? +d[idField] : i);
      })
      .attr("height", function(d) {
        return Math.max(
          0.0000001,
          svg.y(d.low) - svg.y(Math.min(d.open, d.close)),
        );
      })
      .attr("width", 1)
      .style("fill", function(d) {
        return d.close > d.open ? upColor : dnColor;
      })
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
  }

  // add candle bodies
  svg.candle = svgAux
    .selectAll(".candles" + customName)
    .data([data])
    .enter()
    .append("g")
    .attr("class", "candles" + customName);
  svg.candle
    .selectAll("rect") // candle bodies are called candles
    .data(function(d) {
      return d;
    })
    .enter()
    .append("rect")
    .attr("x", function(d) {
      return customX
        ? dateInterpolate(xRange, d.date, svg.x, svg.x.bandwidth()) +
            deltaCustom / 2
        : svg.x(d.date) + delta;
    })
    .attr("y", function(d) {
      return svg.y(max([d.open, d.close]));
    })
    .attr("class", function(d, i) {
      return "candle" + customName + "" + (addId ? +d[idField] : i);
    })
    .attr("height", function(d) {
      return Math.max(
        0.000001,
        svg.y(min([d.open, d.close])) - svg.y(max([d.open, d.close])),
      );
    })
    .attr("width", customX ? deltaCustom : candleWidth)
    .attr("stroke-width", lineWidth)
    .attr("stroke", function(d) {
      return d.close > d.open ? upColor : dnColor;
    })
    .style("fill", function(d) {
      return d.close > d.open ? upColorBackground : dnColorBackground;
    })
    .attr("fill-opacity", function(d) {
      return d.close > d.open ? opacityUp : opacityDn;
    });

  return svg;
};

D3Chart.prototype.bars = function(
  svg,
  data,
  barWidth = 0.7,
  name,
  color = textactive,
  fillColor = textactive,
  xField = "date",
  yField = "value",
  show = true,
  multipleColor = false,
  toZero = true,
  heightAux = 2,
  lineWidth = 1,
  addId = false,
  idField = "id",
  multipleHeight = false,
  lowerField = "",
  offset = 0,
  xIsDate = false,
  bandWidthAux = 0,
  svgAux = null,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  let bandWidth;
  if (xIsDate === false) {
    bandWidth = svg.x.bandwidth();
  } else {
    bandWidth = bandWidthAux;
  }
  const candleWidth =
    Math.floor(min([bandWidth * barWidth, 1000000]) / 2) * 2 + 1; // give space between bars
  const delta = Math.round((bandWidth - candleWidth) / 2); // space given
  offset = offset === 0 ? 0 : offset * bandWidth - delta;

  svg[name] = svgAux
    .selectAll(".bars" + name)
    .data([data])
    .enter()
    .append("g")
    .attr("class", "bars" + name)
    .style("opacity", show);
  svg[name]
    .selectAll("rect")
    .data(function(d) {
      return d;
    })
    .enter()
    .append("rect")
    .filter(function(d) {
      return Number.isFinite(d[yField]);
    })
    .style("fill", function(d, i) {
      return multipleColor ? fillColor[i] : fillColor;
    })
    .style("stroke", function(d, i) {
      return multipleColor ? color[i] : color;
    })
    .style("stroke-width", lineWidth)
    .attr("x", function(d) {
      return svg.x(d[xField]) + delta + offset;
    })
    .attr("y", function(d) {
      return toZero === true
        ? Math.min(svg.y(0), svg.y(d[yField]))
        : svg.y(d[yField]) - (multipleHeight ? 0 : heightAux / 2);
    })
    .attr("class", function(d, i) {
      return "bar" + name + "" + (addId ? +d[idField] : i);
    })
    .attr("height", function(d) {
      return Math.max(
        1,
        toZero === true
          ? Math.abs(svg.y(0) - svg.y(d[yField]))
          : multipleHeight
          ? svg.y(d[lowerField]) - svg.y(d[yField])
          : heightAux,
      );
    })
    .attr("width", candleWidth)
    .style("opacity", function(d) {
      return +isFinite(d[yField]);
    })
    .style("shape-rendering", "crispEdges");
};

D3Chart.prototype.hBars = function(
  svg,
  data,
  barWidth = 0.7,
  name,
  color = textactive,
  fillColor = textactive,
  fillColorAlpha = 0.5,
  xField = "x",
  yField = "name",
  show = true,
  multipleColor = false,
  toZero = true,
  widthAux = 2,
  multipleWidth = false,
  strokeWidth = 1,
  addId = false,
  idField = "id",
) {
  var bandWidth = svg.y.bandwidth(); // from scale
  var candleWidth =
    Math.floor(min([bandWidth * barWidth, 1000000]) / 2) * 2 + 1; // give space between bars
  var delta = Math.round((bandWidth - candleWidth) / 2); // space given

  svg.hBar = svg
    .selectAll(".hbars" + name)
    .data([data])
    .enter()
    .append("g")
    .attr("class", "hbars" + name)
    .style("opacity", show);
  svg.hBar
    .selectAll("rect")
    .data(function(d) {
      return d;
    })
    .enter()
    .append("rect")
    .filter(function(d, i) {
      return (
        (toZero === true
          ? Math.abs(svg.x(0) - svg.x(d[xField]))
          : multipleWidth === true
          ? Math.abs(svg.x(0) - svg.x(widthAux[i]))
          : widthAux) > 0
      );
    })
    .style("fill", function(d, i) {
      return multipleColor ? fillColor[i] : fillColor;
    })
    .style("fill-opacity", fillColorAlpha)
    .style("stroke", function(d, i) {
      return multipleColor ? color[i] : color;
    })
    .style("stroke-width", strokeWidth)
    .attr("x", function(d) {
      return toZero === true
        ? Math.min(svg.x(0), svg.x(d[xField]))
        : svg.x(d[xField]);
    })
    .attr("y", function(d) {
      return svg.y(d[yField]) + delta;
    })
    .attr("class", function(d) {
      return "hbar" + name + "" + (addId ? d[idField] : d.currency);
    })
    .attr("width", function(d, i) {
      return toZero === true
        ? Math.abs(svg.x(0) - svg.x(d[xField]))
        : multipleWidth === true
        ? Math.abs(svg.x(0) - svg.x(widthAux[i]))
        : widthAux;
    })
    .attr("height", candleWidth)
    .style("shape-rendering", "crispEdges");

  return svg;
};

D3Chart.prototype.twoStackedBars = function(
  svg,
  data,
  barWidth = 0.7,
  name1,
  name2,
  color = textactive,
  fillColor = textactive,
  color2 = textactive,
  fillColor2 = textactive,
  xField = "date",
  yField1 = "balance",
  yField2 = "cash_flow",
  show = true,
  strokeWidth = 1,
  offset = 0,
  svgAux = null,
  addId = false,
  idField = "id",
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  var bandWidth = svg.x.bandwidth();
  var candleWidth =
    Math.floor(min([bandWidth * barWidth, 1000000]) / 2) * 2 + 1;
  var delta = Math.round((bandWidth - candleWidth) / 2);
  offset = offset === 0 ? 0 : offset * bandWidth - delta;

  svg["stackedBar1"] = svgAux
    .selectAll(".stackedBars1" + name1)
    .data([data])
    .enter()
    .append("g")
    .attr("class", "stackedBars1" + name1)
    .style("opacity", show);
  svg["stackedBar1"]
    .selectAll("rect") // balance exposure bars are called stackedBar1
    .data(function(d) {
      return d;
    })
    .enter()
    .append("rect")
    .style("fill", fillColor)
    .style("stroke", color)
    .style("stroke-width", strokeWidth)
    .attr("x", function(d) {
      return svg.x(d[xField]) + delta + offset;
    })
    .attr("y", function(d) {
      return Math.min(svg.y(0), svg.y(d[yField1]));
    })
    .attr("class", function(d, i) {
      return "stackedBar1" + name1 + "" + (addId ? +d[idField] : i);
    })
    .attr("height", function(d) {
      return Math.abs(svg.y(0) - svg.y(d[yField1]));
    })
    .attr("width", candleWidth)
    .style("shape-rendering", "crispEdges");

  svg["stackedBar2"] = svgAux
    .selectAll(".stackedBars2" + name2)
    .data([data])
    .enter()
    .append("g")
    .attr("class", "stackedBars2" + name2)
    .style("opacity", show);
  svg["stackedBar2"]
    .selectAll("rect") // cash flow exposure bars are called stackedBar2
    .data(function(d) {
      return d;
    })
    .enter()
    .append("rect")
    .style("fill", fillColor2)
    .style("stroke", color2)
    .style("stroke-width", strokeWidth)
    .attr("x", function(d) {
      return svg.x(d[xField]) + delta + offset;
    })
    .attr("y", function(d) {
      return Math.min(svg.y(d[yField2] + d[yField1]), svg.y(d[yField1]));
    })
    .attr("class", function(d, i) {
      return "stackedBar2" + name2 + "" + (addId ? +d[idField] : i);
    })
    .attr("height", function(d) {
      return Math.abs(svg.y(0) - svg.y(d[yField2]));
    })
    .attr("width", candleWidth);
};

D3Chart.prototype.multipleStackedBars = function(
  svg,
  data,
  barWidth,
  nameBar,
  colors,
  fillColors,
  xField = "date",
  yFields,
  show = true,
  strokeWidth = 1,
  offset = 0,
  svgAux = null,
  addId = false,
  idField = "id",
) {
  if (svgAux === null) {
    svgAux = svg;
  }

  var bandWidth = svg.x.bandwidth();
  var candleWidth =
    Math.floor(min([bandWidth * barWidth, 1000000]) / 2) * 2 + 1;
  var delta = Math.round((bandWidth - candleWidth) / 2);
  offset = offset === 0 ? 0 : offset * bandWidth - delta;

  yFields.forEach((barName, i) => {
    const yField = yFields[i];
    const color = colors[i];
    const fillColor = fillColors[i];

    svg[`stackedBar`] = svgAux
      .selectAll(`.stackedBars${nameBar[i]}`)
      .data([data])
      .enter()
      .append("g")
      .attr("class", `stackedBars${nameBar[i]}`)
      .style("opacity", show);

    svg[`stackedBar`]
      .selectAll("rect")
      .data(function(d) {
        return d;
      })
      .enter()
      .append("rect")
      .style("fill", fillColor)
      .style("stroke", color)
      .style("stroke-width", strokeWidth)
      .attr("x", function(d) {
        return svg.x(d[xField]) + delta + offset;
      })
      .attr("y", function(d) {
        let accumulated = 0;
        for (let j = 0; j <= i; j++) {
          if (!Number.isNaN(d[yFields[j]])) accumulated += +d[yFields[j]];
        }
        return svg.y(accumulated);
      })
      .attr("class", function(d, dataIndex) {
        return `stackedBar${nameBar[i]}${addId ? +d[idField] : dataIndex}`;
      })
      .attr("height", function(d) {
        if (!Number.isNaN(d[yField]))
          return Math.abs(svg.y(0) - svg.y(d[yField]));
      })
      .attr("width", candleWidth)
      .style("shape-rendering", "crispEdges");
  });
};

D3Chart.prototype.line = function(
  svg,
  data,
  color,
  lineWidth = 2,
  fieldMame = "value",
  customX = false,
  deltaBand = 1 / 22,
  xRange = [],
  xIsDate = false,
  fieldLine = "line",
  svgAux = null,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  let bandWidth;
  if (xIsDate === false) {
    bandWidth = svg.x.bandwidth();
  } else {
    bandWidth = 0;
  }
  const deltaCustom = bandWidth * deltaBand;

  var line = lineD3()
    .curve(curveMonotoneX)
    .defined((d) => d[fieldMame])
    .x((d) =>
      customX
        ? dateInterpolate(xRange, d.date, svg.x, bandWidth) +
          deltaCustom -
          lineWidth / 2
        : svg.x(d.date) + bandWidth / 2,
    )
    .y((d) => svg.y(d[fieldMame]));

  svg[fieldLine] = svgAux
    .append("g")
    .attr("fill", "none")
    .attr("stroke-width", lineWidth)
    .attr("stroke-linejoin", "round")
    .attr("stroke-linecap", "round")
    .style("shape-rendering", "crispEdges")
    .selectAll("path")
    .data([data])
    .join("path")
    .style("stroke", color)
    .attr("d", (d) => line(d))
    .style("vector-effect", "non-scaling-stroke");

  return svg;
};

D3Chart.prototype.line2Axis = function(
  svg,
  data,
  color,
  lineWidth = 2,
  fieldMame = "value",
  xIsDate = false,
  fieldLine = "line",
  svgAux = null,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  let bandWidth;
  if (xIsDate === false) {
    bandWidth = svg.x.bandwidth();
  } else {
    bandWidth = 0;
  }

  var line = lineD3()
    .curve(curveMonotoneX)
    .defined((d) => d[fieldMame])
    .x((d) => svg.x(d.date) + bandWidth / 2)
    .y((d) => svg.y2(d[fieldMame]));

  svg[fieldLine] = svgAux
    .append("g")
    .attr("fill", "none")
    .attr("stroke-width", lineWidth)
    .attr("stroke-linejoin", "round")
    .attr("stroke-linecap", "round")
    .selectAll("path")
    .data([data])
    .join("path")
    .style("stroke", color)
    .attr("d", (d) => line(d))
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");
};

D3Chart.prototype.multiLine = function(
  svg,
  data,
  colorSelection,
  lineWidth = 2,
  selectedVar = -1,
  useDefined = true,
  name = "name",
) {
  var bandWidth = svg.x.bandwidth();
  var line;
  if (useDefined) {
    line = lineD3()
      .curve(curveLinear) // .curve(curveMonotoneX)
      .defined((d) => +d)
      .x((d, i) => svg.x(data.dates[i]) + bandWidth / 2)
      .y((d) => svg.y(d));
  } else {
    line = lineD3()
      .curve(curveLinear)
      .x((d, i) => svg.x(data.dates[i]) + bandWidth / 2)
      .y((d) => svg.y(d));
  }

  svg.path = svg
    .append("g")
    .attr("class", "multiLinesGroup")
    .attr("fill", "none")
    .attr("stroke-linejoin", "round")
    .attr("stroke-linecap", "round")
    .selectAll("path")
    .data(data.series)
    .join("path")
    .style("stroke", (d, i) => colorSelection[i])
    .style("stroke-width", lineWidth)
    .attr("class", function(d, i) {
      let nameNonSpaces = cleanAndUnionName(d[name]);
      return `multiLines${nameNonSpaces ? nameNonSpaces : i}`;
    })
    .attr("d", (d) => line(d.values))
    .classed("highlighted", function(d, i) {
      return i === selectedVar;
    })
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");

  return svg;
};

D3Chart.prototype.orderLines = function(
  svg,
  sortedData,
  color,
  lineWidth = 2,
  selectedVar = -1,
  nameField = "short_name",
  latestField = "latest",
  idx = "idx",
  onMouseOut = false,
  onHoverElements = false,
  isEvent = false,
  zoomX = 1,
) {
  zoomX = Math.min(Math.max(0.5, zoomX), 1);
  let lineXPoints = [
    svg.xRight(0),
    svg.xRight((1 / 8) * svg.xMax),
    svg.xRight((3 / 8) * svg.xMax),
    svg.xRight((1 / 2) * svg.xMax),
  ];
  lineXPoints[0] =
    lineXPoints[0] + (1 - zoomX) * (lineXPoints[3] - lineXPoints[0]);
  lineXPoints[1] =
    lineXPoints[1] + (1 - zoomX) * (lineXPoints[3] - lineXPoints[1]);
  lineXPoints[2] =
    lineXPoints[2] + (1 - zoomX) * (lineXPoints[3] - lineXPoints[2]);
  // right sorting lines - 1
  svg.sortlines1 = svg
    .append("g")
    .selectAll(".sortLineOnes")
    .data([sortedData])
    .enter()
    .append("g")
    .attr("class", "sortLineOnes")
    .attr("id", "sortLinesid")
    .selectAll("line")
    .data(function() {
      return sortedData.map(function(d, i) {
        return { name: d[nameField], value: d[latestField], idx: i };
      });
    })
    .enter()
    .append("line")
    .attr("x1", lineXPoints[0])
    .attr("x2", lineXPoints[1] + 1)
    .attr("y1", function(d) {
      return svg.y(d.value);
    })
    .attr("y2", function(d) {
      return svg.y(d.value);
    })
    .style("opacity", function(d) {
      return +isFinite(d.value);
    })
    .attr("class", function(d) {
      let nameNonSpaces = d[idx];
      if (idx === "name") {
        nameNonSpaces = cleanAndUnionName(nameNonSpaces);
      }
      return "sortLineOne" + nameNonSpaces;
    })
    .style("stroke-width", lineWidth)
    .style("display", null)
    .attr("stroke", function(d) {
      return color(isFinite(d.value) ? d.value : 0);
    })
    .classed("highlightedBind", function(d) {
      let nameNonSpaces = d[idx];
      if (idx === "name") {
        nameNonSpaces = cleanAndUnionName(nameNonSpaces);
      }
      return nameNonSpaces === selectedVar;
    })
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke")
    .on("mouseover", function(d) {
      if (isEvent) {
        onHoverElements(d);
      }
    })
    .on("mouseout", function() {
      if (isEvent) {
        onMouseOut();
      }
    });

  // right sorting lines - 2
  svg.sortlines2 = svg
    .append("g")
    .selectAll(".sortLineTwos")
    .data([sortedData])
    .enter()
    .append("g")
    .attr("class", "sortLineTwos")
    .attr("id", "sortLine2sid")
    .selectAll("line")
    .data(function() {
      return sortedData.map(function(d, i) {
        return { name: d[nameField], value: d[latestField], idx: i };
      });
    })
    .enter()
    .append("line")
    .attr("x1", lineXPoints[1])
    .attr("x2", lineXPoints[2])
    .attr("y1", function(d) {
      return svg.y(d.value);
    })
    .attr("y2", function(d) {
      return svg.height - svg.step * d.idx - svg.step / 2;
    })
    .style("opacity", function(d) {
      return +isFinite(d.value);
    })
    .attr("class", function(d) {
      let nameNonSpaces = d[idx];
      if (idx === "name") {
        nameNonSpaces = cleanAndUnionName(nameNonSpaces);
      }
      return "sortLineTwo" + nameNonSpaces;
    })
    .style("stroke-width", lineWidth)
    .style("display", null)
    .attr("stroke", function(d) {
      return color(isFinite(d.value) ? d.value : 0);
    })
    .classed("highlightedBind", function(d) {
      let nameNonSpaces = d[idx];
      if (idx === "name") {
        nameNonSpaces = cleanAndUnionName(nameNonSpaces);
      }
      return nameNonSpaces === selectedVar;
    })
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke")
    .on("mouseover", function(d) {
      if (isEvent) {
        onHoverElements(d);
      }
    })
    .on("mouseout", function() {
      if (isEvent) {
        onMouseOut();
      }
    });

  // right sorting lines - 3
  svg.sortlines3 = svg
    .append("g")
    .selectAll(".sortLineThrees")
    .data([sortedData])
    .enter()
    .append("g")
    .attr("class", "sortLineThrees")
    .attr("id", "sortLine3sid")
    .selectAll("line")
    .data(function() {
      return sortedData.map(function(d, i) {
        return { name: d[nameField], value: d[latestField], idx: i };
      });
    })
    .enter()
    .append("line")
    .attr("x1", lineXPoints[2] - 1)
    .attr("x2", lineXPoints[3])
    .attr("y1", function(d) {
      return svg.height - svg.step * d.idx - svg.step / 2;
    })
    .attr("y2", function(d) {
      return svg.height - svg.step * d.idx - svg.step / 2;
    })
    .style("opacity", function(d) {
      return +isFinite(d.value);
    })
    .attr("class", function(d) {
      let nameNonSpaces = d[idx];
      if (idx === "name") {
        nameNonSpaces = cleanAndUnionName(nameNonSpaces);
      }
      return "sortLineThree" + nameNonSpaces;
    })
    .style("stroke-width", lineWidth)
    .style("display", null)
    .attr("stroke", function(d) {
      return color(isFinite(d.value) ? d.value : 0);
    })
    .classed("highlightedBind", function(d) {
      let nameNonSpaces = d[idx];
      if (idx === "name") {
        nameNonSpaces = cleanAndUnionName(nameNonSpaces);
      }
      return nameNonSpaces === selectedVar;
    })
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke")
    .on("mouseover", function(d) {
      if (isEvent) {
        onHoverElements(d);
      }
    })
    .on("mouseout", function() {
      if (isEvent) {
        onMouseOut();
      }
    });

  return svg;
};

D3Chart.prototype.orderButtons = function(
  svg,
  sortedData,
  color,
  selectedVar = -1,
  fontColor,
  nameField = "short_name",
  latestField = "latest",
  nameAuxField = "short_name",
  fontSize = 0.8,
  opacity = 1,
  strokeWidth = 2,
  name = "NAME",
  idx = "idx",
  isEvent = false,
  onMouseOut = false,
  onHoverElements = false,
  onClickLegends = false,
  direction = "right",
  customText = false,
  gap = 1,
) {
  const xPosition = (dir = direction) =>
    dir === "right" ? (1 / 2) * svg.xMax : (5 / 8) * svg.xMax;

  const rectTransform = function() {
    if (direction === "right") {
      return svg.xRight(xPosition()) + 3 * gap;
    } else {
      return svg.xLeft(xPosition()) + 3 * gap;
    }
  };

  const textTransform = function(dir) {
    if (direction === "right") {
      return svg.xRight(xPosition(dir)) + 3 * gap;
    } else {
      return svg.xLeft(xPosition(dir)) + 3 * gap;
    }
  };

  const rectWidth = function() {
    return svg.xRight((1 / 8) * svg.xMax) - svg.xRight(0) - gap * 2;
  };

  svg.legendsB = svg
    .append("g")
    .selectAll(".legends")
    .data([sortedData])
    .enter()
    .append("g")
    .attr("class", "legendB")
    .attr("id", "legendid")
    .selectAll("rect")
    .data(function() {
      return sortedData.map(function(d, i) {
        return {
          name: d[nameField],
          aux_name: d[nameAuxField],
          value: d[latestField],
          idx: i,
        };
      });
    })
    .enter()
    .append("rect")
    .attr("x", rectTransform)
    .attr("y", function(d) {
      return (
        svg.height -
        svg.step * d.idx -
        (svg.xRight((1 / 8) * svg.xMax) - svg.xRight(0)) / 2 +
        gap -
        svg.step / 2
      );
    })
    .attr("class", function(d) {
      const nameNonSpaces = cleanAndUnionName(d.name);
      return "legendB" + name + "" + nameNonSpaces;
    })
    .attr("width", rectWidth)
    .attr("height", rectWidth)
    .attr("fill", function(d) {
      return color(isFinite(d.value) ? d.value : 0);
    })
    .attr("fill-opacity", opacity)
    .attr("stroke", function(d) {
      return color(isFinite(d.value) ? d.value : 0);
    })
    .attr("stroke-width", strokeWidth)
    .classed("highlightedBind", function(d) {
      return d.idx === selectedVar;
    })
    .on("click", function(d) {
      if (isEvent) {
        onClickLegends(d, name);
      }
    })
    .on("mouseover", function(d) {
      if (isEvent) {
        onHoverElements(d, name);
      }
    })
    .on("mouseout", function() {
      if (isEvent) {
        onMouseOut();
      }
    });

  // buttons name
  svg.legendsT = svg
    .append("g")
    .selectAll(".sortTexts")
    .data([sortedData])
    .enter()
    .append("g")
    .attr("class", "sortTexts")
    .attr("id", "sortTextsid")
    .selectAll("text")
    .data(function() {
      return sortedData.map(function(d, i) {
        return {
          name: d[nameField],
          aux_name: d[nameAuxField],
          idx: i,
          currRepresentation: d?.currRepresentation,
        };
      });
    })
    .enter()
    .append("text")
    .attr("transform", function(d) {
      return `translate(${textTransform(d, "left")},
      ${svg.height - svg.step * d.idx - svg.step / 2} )`;
    })
    .attr("dx", ".71em")
    .style("text-anchor", "start")
    .style("alignment-baseline", "central")
    .style("fill", fontColor)
    .style("font-size", fontSize + "rem")
    .text(function(d) {
      if (customText) {
        return `${d.name} ${d.currRepresentation}`;
      } else {
        return d.name;
      }
    })
    .attr("class", function(d) {
      let nameNonSpaces = d[idx];
      if (idx === "name") {
        nameNonSpaces = cleanAndUnionName(nameNonSpaces);
      }
      return "sortText" + nameNonSpaces;
    })
    .classed("highlightedBind", function(d) {
      let nameNonSpaces = d[idx];
      if (idx === "name") {
        nameNonSpaces = cleanAndUnionName(nameNonSpaces);
      }
      return nameNonSpaces === selectedVar;
    });

  return svg;
};

D3Chart.prototype.linesArea = function(
  svg,
  data,
  line1Color,
  line2Color,
  lineWidth = 1,
  areaColor,
  upperName = "upper",
  lowerName = "lower",
  firstLineDashed = false,
  lineWidth2 = 1,
  secondLineDashed = false,
  xIsDate = false,
  fieldUpperLine = "upperLine",
  fieldLowerLine = "lowerLine",
  fieldArea = "area",
  svgAux = null,
  drawArea = true,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  let bandWidth;
  if (xIsDate === false) {
    bandWidth = svg.x.bandwidth();
  } else {
    bandWidth = 0;
  }
  var upperLine = lineD3()
    .defined((d) => definedFct(d[upperName]))
    .x((d) => svg.x(d.date) + bandWidth / 2)
    .y((d) => svg.y(d[upperName]));
  var lowerLine = lineD3()
    .defined((d) => definedFct(d[lowerName]))
    .x((d) => svg.x(d.date) + bandWidth / 2)
    .y((d) => svg.y(d[lowerName]));
  // ======= Area =======
  if (drawArea) {
    var area = areaD3()
      .defined((d) => definedFct(d[upperName]))
      .defined((d) => definedFct(d[lowerName]))
      .x((d) => svg.x(d.date) + bandWidth / 2)
      .y0((d) => svg.y(d[upperName]))
      .y1((d) => svg.y(d[lowerName]));
    // ======= Area in between =======
    svg[fieldArea] = svgAux
      .append("path")
      .datum(data)
      .attr("fill", areaColor)
      .attr("d", area);
  }
  // ======= Data join for paths =======
  if (firstLineDashed === true) {
    svg[fieldUpperLine] = svgAux
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", line1Color)
      .attr("stroke-width", lineWidth)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .attr("d", upperLine)
      .style("stroke-dasharray", "5, 5")
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
  } else {
    svg[fieldUpperLine] = svgAux
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", line1Color)
      .attr("stroke-width", lineWidth)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .attr("d", upperLine)
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
  }

  // ======= Data join for paths =======
  if (secondLineDashed === true) {
    svg[fieldLowerLine] = svgAux
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", line2Color)
      .attr("stroke-width", lineWidth2)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .attr("d", lowerLine)
      .style("stroke-dasharray", "5, 5")
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
  } else {
    svg[fieldLowerLine] = svgAux
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", line2Color)
      .attr("stroke-width", lineWidth2)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .attr("d", lowerLine)
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
  }
};

D3Chart.prototype.multiLinesArea = function(
  svg,
  data,
  lineColor,
  lineWidth = 1,
  areaColor,
  nameList,
  linesDashed = false,
  xIsDate = false,
  fieldLine = "fieldLine",
  svgAux = null,
  drawArea = true,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  let bandWidth;
  if (xIsDate === false) {
    bandWidth = svg.x.bandwidth();
  } else {
    bandWidth = 0;
  }
  let lineFct, areaFct, fieldName1, fieldName2;
  let dashArray = linesDashed === true ? "5, 5" : null;
  for (let i = 0; i < nameList.length - 1; i++) {
    fieldName1 = nameList[i];
    fieldName2 = nameList[i + 1];
    if (drawArea) {
      // ======= Area =======
      areaFct = areaD3()
        .defined((d) => d[fieldName1])
        .defined((d) => d[fieldName2])
        .x((d) => svg.x(d.date) + bandWidth / 2)
        .y0((d) => svg.y(d[fieldName1]))
        .y1((d) => svg.y(d[fieldName2]));
      // ======= Area between lines =======
      svg[fieldLine + "Area" + i] = svgAux
        .append("path")
        .datum(data)
        .attr("fill", areaColor[i])
        .attr("d", areaFct);
    }
    // ======= Line =======
    lineFct = lineD3()
      .defined((d) => d[fieldName1])
      .x((d) => svg.x(d.date) + bandWidth / 2)
      .y((d) => svg.y(d[fieldName1]));
    svg[fieldLine + "Line" + i] = svgAux
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", lineColor[i])
      .attr("stroke-width", lineWidth)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .attr("d", lineFct)
      .style("stroke-dasharray", dashArray)
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
    if (i === nameList.length - 2) {
      lineFct = lineD3()
        .defined((d) => d[fieldName2])
        .x((d) => svg.x(d.date) + bandWidth / 2)
        .y((d) => svg.y(d[fieldName2]));
      svg[fieldLine + "Line" + (i + 1)] = svgAux
        .append("path")
        .datum(data)
        .attr("fill", "none")
        .attr("stroke", lineColor[i + 1])
        .attr("stroke-width", lineWidth)
        .attr("stroke-linejoin", "round")
        .attr("stroke-linecap", "round")
        .attr("d", lineFct)
        .style("stroke-dasharray", dashArray)
        .style("shape-rendering", "crispEdges")
        .style("vector-effect", "non-scaling-stroke");
    }
  }
};

D3Chart.prototype.pie = function(
  svg,
  data,
  outerRadius = null,
  innerRadius = null,
  anglePadding = 0,
  color,
  fillAlpha = 1,
  lineWidth = 1,
  strokeOpacity = 1,
  innerRadiusVsOuter = 2 / 3,
  borderHover,
  endAngle = null,
  mouseover,
  mouseout,
) {
  const width = svg.width,
    height = svg.height;
  if (outerRadius == null) {
    outerRadius = Math.min(width, height);
  }
  if (innerRadius == null) {
    innerRadius = innerRadiusVsOuter * outerRadius;
  }
  if (endAngle == null) {
    svg.pie = pie()
      .value(function(d) {
        return d;
      })
      .sort(null);
  } else {
    svg.pie = pie()
      .value(function(d) {
        return d;
      })
      .endAngle(endAngle)
      .sort(null);
  }
  let arc = arcD3()
    .padAngle(anglePadding)
    .innerRadius(outerRadius)
    .outerRadius(innerRadius);
  svg
    .datum(data)
    .selectAll("path")
    .data(svg.pie)
    .enter()
    .append("path")
    .attr("stroke-width", lineWidth)
    .attr("stroke", function(d, i) {
      return color[i];
    })
    .attr("stroke-opacity", strokeOpacity)
    .attr("fill", function(d, i) {
      return color[i];
    })
    .attr("fill-opacity", fillAlpha)
    .each(function(d) {
      d.outerRadius = outerRadius;
    })
    .attr("d", arc)
    .on("mouseover", mouseover)
    .on("mousemove", mouseover)
    .on("mouseout", mouseout)
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");
  return svg;
};

D3Chart.prototype.horizontalLine = function(
  svg,
  level,
  color,
  lineWidth = 2,
  upperArea = false,
  lowerArea = false,
  areaColor = null,
) {
  if (upperArea === true) {
    svg
      .append("rect")
      .attr("fill", areaColor)
      .attr("x", 0)
      .attr("y", 0)
      .attr("height", svg.y(level))
      .attr("width", svg.width)
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
  }
  if (lowerArea === true) {
    svg
      .append("rect")
      .attr("fill", areaColor)
      .attr("x", 0)
      .attr("y", svg.y(level))
      .attr("height", svg.height - svg.y(level))
      .attr("width", svg.width)
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
  }
  svg
    .append("line")
    .attr("stroke-width", lineWidth)
    .attr("stroke", color)
    .attr("x1", 0)
    .attr("y1", svg.y(level))
    .attr("x2", svg.width)
    .attr("y2", svg.y(level))
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");
};

D3Chart.prototype.backgroundRect = function(
  svg,
  xDates,
  xCondition,
  customName,
  delta = 0,
  correct = 0,
  svgAux = null,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  var bandWidth = svg.x.bandwidth();

  // include group background style
  var bkgrnd = svgAux
    .selectAll(".bkgrnd" + customName)
    .data([xDates])
    .enter()
    .append("g")
    .attr("class", "bkgrnd" + customName);
  bkgrnd
    .selectAll("rect")
    .data(function(d) {
      return d;
    })
    .enter()
    .append("rect")
    .attr("x", function(d) {
      return svg.x(d) + delta;
    })
    .attr("y", 0)
    .attr("height", svg.height)
    .attr("width", bandWidth - 2 * delta + correct)
    .attr("fill", "none")
    .classed("highlight", function(d, i) {
      return xCondition[i] === true;
    });

  return svg;
};

D3Chart.prototype.text = function(
  svg,
  data,
  xField = "date",
  yField = "y",
  textField = "text",
  name,
  fontSize = "12px",
  xOffset = 0,
  yOffset = 0,
  textAnchor = "middle",
  alignmentBaseline = "ideographic",
  rotate = 0,
  textColor = textactive(true),
  isYAxisNumber = true,
  svgAux = null,
  formatText = null,
  yFields = null,
  index = null,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  svg[name] = svgAux
    .selectAll(".text" + name)
    .data(data)
    .enter()
    .append("text")
    .attr("class", ".text" + name)
    .filter(function(d) {
      return isYAxisNumber ? Number.isFinite(d[yField]) : true;
    })
    .attr("transform", function(d) {
      let accumulated = 0;
      if (yFields) {
        let differenceYFieldAccum;
        accumulated = yFields
          .slice(0, index + 1)
          .filter((fieldName) => !Number.isNaN(d[fieldName]))
          .reduce((sum, fieldName) => {
            const sumField = sum + +d[fieldName];
            differenceYFieldAccum = svg.y(d[fieldName]) - svg.y(sumField);
            return sumField;
          }, 0);
        accumulated = svg.y(0) - differenceYFieldAccum - 5;
      } else {
        accumulated = svg.y(d[yField]) + yOffset;
      }
      return `translate(${svg.x(d[xField]) +
        Number(xOffset)}, ${accumulated}) rotate(${rotate})`;
    })
    .attr("x", 0)
    .style("fill", textColor)
    .style("text-anchor", textAnchor)
    .style("alignment-baseline", alignmentBaseline)
    .style("font-size", fontSize)
    .text(function(d) {
      return formatText ? formatText(d[textField]) : d[textField];
    });
};

D3Chart.prototype.contourVols = function(
  svg,
  data,
  lineColor = textactive(true),
  opacity = 0.25,
  customColors = false,
  colorSelection,
  d3Colors,
  strokeOpacity = 1,
  colorTheme = true,
) {
  // Source -> https://observablehq.com/@d3/contours
  var bandWidth = svg.x.bandwidth();

  let minY = svg.y.ticks()[0];
  let maxY = svg.y.ticks()[svg.y.ticks().length - 1];

  // const q = bandWidth; // The level of detail, e.g., sample every 4 pixels in x and y.
  const n = data.length;
  const m = 200;
  const y0 = minY;
  const delta = (maxY - minY) / m;
  const grid = new Array(n * m);
  for (let j = 0; j < m; ++j) {
    for (let i = 0; i < n; ++i) {
      grid[j * n + i] = qVolatility(data[i], y0 + j * delta);
    }
  }
  let minVol =
    min(grid, function(d) {
      return Number.isFinite(d) ? d : Infinity;
    }) - 0.001;
  let maxVol =
    max(grid, function(d) {
      return Number.isFinite(d) ? d : -Infinity;
    }) + 0.001;
  grid.n = n;
  grid.m = m;

  function indexInterpolate(x, n, xAxisFct, data, field, bandWidth) {
    let idx = Math.min(n - 1, Math.floor(x));
    let remainder = x - idx;
    return xAxisFct(data[idx][field]) + bandWidth * remainder;
  }

  // Converts from grid coordinates (indexes) to screen coordinates (pixels).
  let transform = ({ type, value, coordinates }) => {
    return {
      type,
      value,
      coordinates: coordinates.map((rings) => {
        return rings.map((points) => {
          return points.map(([x, y]) => [
            indexInterpolate(x, n, svg.x, data, "date", bandWidth),
            svg.y(y0 + delta * y),
          ]);
        });
      }),
    };
  };

  let nThresholds = 10;
  let deltaVol = (maxVol - minVol) / nThresholds;
  let thresholds = range(0, nThresholds + 1).map((i) => minVol + i * deltaVol);
  let color; // Data and color scale
  if (customColors === false) {
    color = colorTheme
      ? d3Colors.domain([maxVol, minVol]).nice()
      : d3Colors.domain([minVol, maxVol]).nice();
  } else {
    let nColorSelection = colorSelection.length;
    let deltaCol = (maxVol - minVol) / nColorSelection;
    let interp = scaleLinear()
      .domain(range(0, nColorSelection + 1).map((i) => minVol + i * deltaCol))
      .range(colorSelection)
      .interpolate(interpolateRgb);
    colorSelection = thresholds.map((d) => interp(d));
    color = scaleLinear()
      .domain(thresholds)
      .range(colorSelection)
      .interpolate(interpolateRgb); //interpolateHsl interpolateHcl interpolateRgb;
  }

  let contours = contoursD3()
    .smooth(true)
    .size([grid.n, grid.m])
    .thresholds(thresholds)(grid)
    .map(transform);

  svg
    .append("g")
    .attr("fill", "none")
    .attr("stroke", lineColor)
    .attr("stroke-opacity", strokeOpacity)
    .selectAll("path")
    .data(contours)
    .join("path")
    .attr("fill", (d) => color(d.value))
    .attr("fill-opacity", opacity)
    .attr("d", geoPath());

  return [contours, grid, color];
};

D3Chart.prototype.vColorLegend = function(
  svg,
  scale,
  ticks,
  legendMargin,
  yDomain,
  legendFullWidth = 50,
  opacity = 1,
  format = ",.1%",
) {
  var legendFullHeight = svg.height + svg.margin.top + svg.margin.bottom;
  // use same margins as main plot
  var legendWidth = legendFullWidth - legendMargin.left - legendMargin.right;
  var legendHeight = legendFullHeight - legendMargin.top - legendMargin.bottom;

  // append gradient bar
  var gradient = svg
    .append("defs")
    .append("linearGradient")
    .attr("id", "gradient")
    .attr("x1", "0%") // bottom
    .attr("y1", "100%")
    .attr("x2", "0%") // to top
    .attr("y2", "0%")
    .attr("spreadMethod", "pad");

  // programmatically generate the gradient for the legend
  // this creates an array of [pct, colour] pairs as stop
  function linspace(start, end, n) {
    var out = [];
    var delta = (end - start) / (n - 1);
    var i = 0;
    while (i < n - 1) {
      out.push(start + i * delta);
      i++;
    }
    out.push(end);
    return out;
  }
  // values for legend
  var pct = linspace(0, 100, scale.length).map(function(d) {
    return Math.round(d) + "%";
  });
  var colourPct = zip(pct, scale);
  colourPct.forEach(function(d) {
    gradient
      .append("stop")
      .attr("offset", d[0])
      .attr("stop-color", d[1])
      .attr("stop-opacity", opacity);
  });
  svg
    .append("rect")
    .attr("x1", 0)
    .attr("y1", 0)
    .attr("width", legendWidth)
    .attr("height", legendHeight)
    .attr(
      "transform",
      "translate(" + (svg.width + 50) + "," + legendMargin.top + ")",
    )
    .style("fill", "url(#gradient)");

  // create a scale and axis for the legend
  let legendScale = scaleLinear()
    .range([legendHeight, 0])
    .domain(yDomain);

  var legendAxis = axisRight(legendAxis)
    .scale(legendScale)
    .ticks(ticks.length)
    .tickSize(-legendWidth)
    .tickValues(ticks)
    .tickFormat(formatD3(format));

  svg
    .append("g")
    .attr("class", "axis axis--y")
    .attr(
      "transform",
      "translate(" +
        (svg.width + legendWidth + legendFullWidth) +
        "," +
        legendMargin.top +
        ")",
    )
    .call(legendAxis);
};

D3Chart.prototype.hColorLegend = function(
  svg,
  scale,
  ticks,
  legendMargin,
  yDomain,
  legendFullWidth = 50,
  opacity = 1,
  format = ",.1%",
  customTicks = null,
) {
  if (customTicks === null) {
    customTicks = ticks;
  }
  var legendFullHeight = svg.width + svg.margin.left + svg.margin.right;
  // use same margins as main plot
  var legendWidth = legendFullWidth - legendMargin.left - legendMargin.right;
  var legendHeight = legendFullHeight - legendMargin.top - legendMargin.bottom;
  // append gradient bar
  var gradient = svg
    .append("defs")
    .append("linearGradient")
    .attr("id", "gradient")
    .attr("x1", "0%") // bottom
    .attr("y1", "100%")
    .attr("x2", "0%") // to top
    .attr("y2", "0%")
    .attr("spreadMethod", "pad");

  // programmatically generate the gradient for the legend
  // this creates an array of [pct, colour] pairs as stop
  function linspace(start, end, n) {
    var out = [];
    var delta = (end - start) / (n - 1);
    var i = 0;
    while (i < n - 1) {
      out.push(start + i * delta);
      i++;
    }
    out.push(end);
    return out;
  }
  // values for legend
  var pct = linspace(0, 100, scale.length).map(function(d) {
    return Math.round(d) + "%";
  });
  var colourPct = zip(pct, scale);
  colourPct.forEach(function(d) {
    gradient
      .append("stop")
      .attr("offset", d[0])
      .attr("stop-color", d[1])
      .attr("stop-opacity", opacity);
  });
  svg
    .append("rect")
    .attr("x1", 0)
    .attr("y1", 0)
    .attr("width", legendWidth)
    .attr("height", legendHeight)
    .attr(
      "transform",
      "translate(" +
        (svg.width + svg.margin.right - legendMargin.top) +
        "," +
        (svg.height + 2.5 * legendWidth) +
        ") rotate(90)",
    )
    .style("fill", "url(#gradient)");

  // create a scale and axis for the legend
  svg.legendScale = scaleLinear()
    .range([legendHeight, 0])
    .domain(yDomain);

  var legendAxis = axisRight(legendAxis)
    .scale(svg.legendScale)
    .ticks(ticks.length)
    .tickSize(-legendWidth)
    .tickValues(ticks)
    .tickFormat((_, i) => formatD3(format)(customTicks[i]));

  svg
    .append("g")
    .attr("class", "axis axis--x")
    .attr("class", "axis")
    .attr(
      "transform",
      "translate(" +
        (svg.width + svg.margin.right - legendMargin.top) +
        "," +
        (svg.height + 3.5 * legendWidth) +
        ") rotate(90)",
    )
    .call(legendAxis)
    .selectAll("text")
    .attr("y", 10)
    .attr("x", -15)
    .attr("dx", ".35em")
    .attr("transform", "rotate(-90)");

  // line that appears on the color bar
  svg.focus = svg
    .append("line")
    .attr("class", "x-hover-line-colormap hover-line-colormap")
    .attr("y1", svg.height + 3.5 * legendWidth)
    .attr("y2", svg.height + 2.5 * legendWidth - 3)
    .attr(
      "x1",
      svg.width + svg.margin.right - legendMargin.top - legendHeight / 2,
    )
    .attr(
      "x2",
      svg.width + svg.margin.right - legendMargin.top - legendHeight / 2,
    )
    .style("stroke-width", "2px")
    .style("stroke", textactive);
};

D3Chart.prototype.correlation = function(
  svg,
  data,
  columns,
  color,
  customName,
  xField = "x",
  yField = "y",
  colorField = "correl",
  lineWidth = 1,
  opacity = 0.5,
  currencyFormat,
  selectedColumn,
  colorTheme,
) {
  let n = Math.sqrt(data.length);
  let tooltipWidth = 120;
  let sharp = "#";
  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("padding", "8px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "auto")
    .html("");

  var mouseOver = function(d, i) {
    select(this)
      .style("stroke", textactive2(colorTheme))
      .style("stroke-width", 2);

    select(".rect1")
      .attr("x", svg.x(data[0][xField]) + 1)
      .attr("y", svg.y(data[0][xField]) + 1)
      .attr("transform", `translate(${svg.x(d.x)},${svg.y(d.x)})`);
    select(".rect2")
      .style("opacity", 1)
      .attr("transform", `translate(${svg.x(d.y)},${svg.y(d.y)})`);
    select(".rect3").style("opacity", 0);

    let offset = -svg.x.bandwidth() / 2;
    let xPos = svg.x(d.x) + svg.x.bandwidth() / 2;
    if (xPos > svg.width / 2) {
      offset = tooltipWidth - svg.x.bandwidth() / 2;
    }
    let posTop, posBottom;
    let yPos = svg.y(d.y) + svg.y.bandwidth() / 2;
    if (yPos > svg.height / 2) {
      posTop = null;
      posBottom =
        svg.height +
        svg.margin.top +
        svg.margin.bottom -
        svg.y(d.y) -
        svg.y.bandwidth() / 2 +
        "px";
    } else {
      posTop = svg.y(d.y) + 1.5 * svg.y.bandwidth() + "px";
      posBottom = null;
    }

    event.preventDefault();
    const xPixels = event.layerX;
    Tooltip.style("opacity", 1)
      .style("color", textactive)
      .style("left", xPixels - offset + "px")
      .style("top", posTop)
      .style("bottom", posBottom)
      .html(
        `<div class="text-center" style="color:var(--textactive)">
        <span style="color:var(--textinactive)">
        ${columns[i % n].toUpperCase()}<br>vs<br>${columns[
          Math.floor(i / n)
        ].toUpperCase()}
        </span>
        <br>ρ: <span style="font-size: 1.2rem">${currencyFormat(
          d.correl,
        )}</span>
        </div>`,
      );
  };
  var mouseLeave = function(d) {
    select(this)
      .style("stroke", color(d[colorField]))
      .style("stroke-width", lineWidth);
    select(".rect1")
      .attr("x", svg.x(selectedColumn) + 1)
      .attr("y", svg.y(selectedColumn) + 1)
      .attr("transform", `translate(0,0)`);
    select(".rect2")
      .attr("x", svg.x(data[0][xField]) + 1)
      .attr("y", svg.y(data[0][xField]) + 1)
      .attr("transform", `translate(0,0)`);
    select(".rect3").style("opacity", 1);
    Tooltip.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
  };

  var xBandWidth = svg.x.bandwidth(); // from scale
  var yBandWidth = svg.y.bandwidth(); // from scale
  // rect 3 for hover
  svg.rect3 = svg
    .append("g")
    .append("rect")
    .style("opacity", 1)
    .attr("fill", null)
    .attr("stroke", textactive2(colorTheme))
    .attr("stroke-width", 2)
    .attr("x", svg.x(selectedColumn) + 1)
    .attr("y", svg.y(data[0][xField]) + 1)
    .attr("class", "rect3")
    .attr("height", yBandWidth - 1)
    .attr("width", xBandWidth - 1)
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");
  // correlation rect with colors
  var rect = svg
    .selectAll(".correls" + customName)
    .data([data])
    .enter()
    .append("g")
    .attr("class", "correls" + customName);
  rect
    .selectAll("rect")
    .data(function(d) {
      return d;
    })
    .enter()
    .append("rect")
    .attr("x", function(d) {
      return svg.x(d[xField]) + 1;
    })
    .attr("y", function(d) {
      return svg.y(d[yField]);
    })
    .attr("class", function(d, i) {
      return "correls" + customName + "" + i;
    })
    .attr("height", yBandWidth - 1)
    .attr("width", xBandWidth - 1)
    .attr("stroke-width", lineWidth)
    .attr("stroke", function(d) {
      return color(d[colorField]);
    })
    .attr("stroke-opacity", opacity)
    .style("fill", function(d) {
      return color(d[colorField]);
    })
    .attr("fill-opacity", function(d) {
      return d[xField] === d[yField] ? 0 : opacity;
    })
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke")
    .on("mouseover", mouseOver)
    .on("mouseleave", mouseLeave)
    .on("mousemove", mouseOver);
  // rect 1 for hover
  svg.rect1 = svg
    .append("g")
    .append("rect")
    .style("opacity", 1)
    .attr("fill", null)
    .attr("stroke", colorFwd)
    .attr("stroke-width", 2)
    .attr("x", svg.x(selectedColumn) + 1)
    .attr("y", svg.y(selectedColumn) + 1)
    .attr("class", "rect1")
    .attr("height", yBandWidth - 1)
    .attr("width", xBandWidth - 1)
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");
  // rect 2 for hover
  svg.rect2 = svg
    .append("g")
    .append("rect")
    .style("opacity", 1)
    .attr("fill", null)
    .attr("stroke", colorFwd)
    .attr("stroke-width", 2)
    .attr("x", svg.x(data[0][xField]) + 1)
    .attr("y", svg.y(data[0][xField]) + 1)
    .attr("class", "rect2")
    .attr("height", yBandWidth - 1)
    .attr("width", xBandWidth - 1)
    .style("shape-rendering", "crispEdges")
    .style("vector-effect", "non-scaling-stroke");
};

D3Chart.prototype.scatterSym = function(
  svg,
  data,
  symbolName,
  rotateDeg,
  fillColor,
  strokeColor,
  xField = "date",
  yField = "value",
  svgAux = null,
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  let d3Symbol;
  if (symbolName === "triangle") {
    d3Symbol = symbolTriangle;
  } else if (symbolName === "circle") {
    d3Symbol = symbolCircle;
  } else if (symbolName === "cross") {
    d3Symbol = symbolCross;
  } else if (symbolName === "diamond") {
    d3Symbol = symbolDiamond;
  } else if (symbolName === "square") {
    d3Symbol = symbolSquare;
  } else if (symbolName === "star") {
    d3Symbol = symbolStar;
  } else if (symbolName === "wye") {
    d3Symbol = symbolWye;
  }
  let bandWidth = svg.x.bandwidth();
  const sym = symbol()
    .type(d3Symbol)
    .size(45);
  svgAux
    .append("g")
    .selectAll("path")
    .data(data)
    .join("path")
    .attr("d", sym)
    .attr("class", symbolName)
    .attr("fill", fillColor)
    .attr("stroke", strokeColor)
    .attr("transform", function(d) {
      return `translate(${svg.x(d[xField]) +
        bandWidth / 2}, ${svg.y(d[yField])}) rotate(${rotateDeg})`;
    });
};

D3Chart.prototype.scatterImg = function(
  svg,
  data,
  groupField,
  customName,
  numberFormat,
  currencyFormat,
  dateFormat,
  baseC,
  imgURL,
  imgURLOff,
  xRange,
  derivativeSize,
  lFwdURL, // normal
  sFwdURL,
  lCallURL,
  sCallURL,
  lPutURL,
  sPutURL,
  lFwdHURL, // highlight
  sFwdHURL,
  lCallHURL,
  sCallHURL,
  lPutHURL,
  sPutHURL,
  lFwdAURL, // alpha
  sFwdAURL,
  lCallAURL,
  sCallAURL,
  lPutAURL,
  sPutAURL,
  lFField,
  sFField,
  lCField,
  sCField,
  lPField,
  sPField,
  showTooltipDeriv,
  isFuture,
  asset,
  adjustTooltip = false,
  colorTheme,
  svgAux = null,
  isAnalytics = false,
  dateFld = "date",
) {
  if (svgAux === null) {
    svgAux = svg;
  }
  let mainFields = [lFField, sFField, lCField, sCField, lPField, sPField];
  let mainUrls = [lFwdURL, sFwdURL, lCallURL, sCallURL, lPutURL, sPutURL];
  let mainHUrls = [
    lFwdHURL,
    sFwdHURL,
    lCallHURL,
    sCallHURL,
    lPutHURL,
    sPutHURL,
  ];
  let mainAUrls = [
    lFwdAURL,
    sFwdAURL,
    lCallAURL,
    sCallAURL,
    lPutAURL,
    sPutAURL,
  ];
  vLineCreate(svg, "RATES", "gray", 1);
  let sharp = "#";
  let tooltipWidth = 500;
  let Tooltip;
  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "auto")
    .html("");
  let tooltipWidth2 = 80;
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .attr("class", "tooltip")
    .style("width", tooltipWidth2 + "px")
    .html("");
  if (showTooltipDeriv) {
    Tooltip = select("body")
      .append("div")
      .style("position", "fixed")
      .style("z-index", "999999")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("width", tooltipWidth + "px")
      .style("padding", "2px")
      .style("background", darkBackground4(colorTheme))
      .attr("class", "tooltip");
  } else {
    Tooltip = select(`${sharp}${svg.parentElement}`)
      .append("div")
      .style("opacity", 0)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("padding", "0")
      .attr("class", "tooltip tooltip-transparent")
      .style("font-size", "0.5rem")
      .style("width", 0.95 * svg.width + "px")
      .style("line-height", "1")
      .html("");
  }

  var mousemove = function(d) {
    event.preventDefault();
    const xPixels = Math.max(
      Math.min(event.layerX - svg.margin.left, svg.width),
      0,
    );

    select(".hHoverLineRATES")
      .style("opacity", 1)
      .attr("y1", svg.y(d.strike))
      .attr("y2", svg.y(d.strike));
    select(".vHoverLineRATES")
      .style("opacity", 1)
      .attr("x1", dateInterpolate(xRange, d[dateFld], svg.x, svg.x.bandwidth()))
      .attr(
        "x2",
        dateInterpolate(xRange, d[dateFld], svg.x, svg.x.bandwidth()),
      );
    select(".hHoverLineTA")
      .style("opacity", 1)
      .attr("y1", svg.y(d.strike))
      .attr("y2", svg.y(d.strike));
    Tooltip2.style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", svg.y(d.strike) + "px")
      .html(currencyFormat(d.strike));

    let txt;
    if (showTooltipDeriv) {
      txt = derivativesTooltipTxt(
        d,
        numberFormat,
        currencyFormat,
        baseC,
        derivativeSize,
        lFwdURL,
        sFwdURL,
        lCallURL,
        sCallURL,
        lPutURL,
        sPutURL,
        isFuture,
        asset,
      );
    } else {
      txt = ratesSmTooltip(
        d,
        numberFormat,
        currencyFormat,
        baseC,
        derivativeSize,
        lFwdURL,
        sFwdURL,
        lCallURL,
        sCallURL,
        lPutURL,
        sPutURL,
        asset,
      );
    }

    let bool = false;
    if (adjustTooltip) {
      let scrnW = document.getElementById("whole-page-div").clientWidth;
      bool =
        0.5 * scrnW +
          dateInterpolate(xRange, d[dateFld], svg.x, svg.x.bandwidth()) +
          svg.margin.left +
          tooltipWidth +
          (scrnW * 2) / 12 >
        scrnW;
    }

    let posAu = xPixels > 0.45 * svg.width || bool ? tooltipWidth + 10 : -10;
    let posBottom;

    if (showTooltipDeriv) {
      let tooltipHeight = Tooltip.node().offsetHeight || 40;
      let cursorX = event.clientX;
      let cursorY = event.clientY;
      // 50% window
      let halfWidth = window.innerWidth / 2;
      let halfHeight = window.innerHeight / 2;
      // left right position
      let posX =
        cursorX < halfWidth
          ? event.pageX + 15
          : event.pageX - tooltipWidth - 15;
      // up/down position
      let posY =
        cursorY < halfHeight
          ? event.pageY + 15
          : event.pageY - tooltipHeight - 15;
      Tooltip.style("opacity", 1)
        .style("left", posX + "px")
        .style("top", posY + "px")
        .html(txt);
    } else {
      Tooltip.style("opacity", 1)
        .style("left", svg.margin.left + 1 + "px")
        .style("top", svg.margin.top + 1 + "px")
        .html(txt);
    }

    posAu =
      showTooltipDeriv && posBottom === null
        ? xPixels < 0.45 * svg.width
          ? tooltipWidth2
          : 0
        : tooltipWidth2 / 2;
    Tooltip3.style("opacity", 1)
      .style(
        "left",
        dateInterpolate(xRange, d[dateFld], svg.x, svg.x.bandwidth()) -
          posAu +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 1 + "px")
      .html(timeFormat(dateFormat)(d[dateFld]));

    select(this)
      .attr(
        "xlink:href",
        derivativeImageHighlight(
          d,
          lFwdHURL,
          sFwdHURL,
          lCallHURL,
          sCallHURL,
          lPutHURL,
          sPutHURL,
        ),
      )
      .attr("width", derivativeSize * 1.2)
      .attr("height", derivativeSize * 1.2);

    for (let i = 0; i < mainFields.length; i++) {
      // --- images highlight - alpha
      try {
        svg[mainFields[i]]
          .selectAll("image")
          .data(function(d) {
            return d;
          })
          .attr("xlink:href", (dat) =>
            dat.strategy_code === d.strategy_code ? mainHUrls[i] : mainAUrls[i],
          );
        // eslint-disable-next-line no-empty
      } catch {}

      // --- rect highlight - alpha
      try {
        svg[mainFields[i] + "Rect"]
          .selectAll("rect")
          .data(function(dat) {
            return dat;
          })
          .attr("stroke", (dat) =>
            barrierColor(mainFields[i], dat.strategy_code !== d.strategy_code),
          )
          .style("fill", function(dat) {
            return dat.derivative_type.indexOf("OUT") > -1
              ? "rgba(0,0,0,0)"
              : barrierColor(
                  mainFields[i],
                  dat.strategy_code !== d.strategy_code,
                );
          });
        // eslint-disable-next-line no-empty
      } catch {}
    }

    // -- historical data line
    if (d.historicalIndex > -1) {
      select(".bandsTA" + d.historicalIndex).classed("hoved", true);
      select(".stickTA" + d.historicalIndex).classed("hoved", true);
      select(".stick2TA" + d.historicalIndex).classed("hoved", true);
      select(".candleTA" + d.historicalIndex).classed("hoved", true);
    }
  };
  var mouseleave = function(d) {
    Tooltip.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    select(".hHoverLineRATES")
      .style("opacity", 0)
      .attr("display", null);
    select(".vHoverLineRATES")
      .style("opacity", 0)
      .attr("display", null);
    select(".hHoverLineTA")
      .style("opacity", 0)
      .attr("display", null);
    Tooltip2.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip3.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    select(this)
      .attr(
        "xlink:href",
        derivativeImage(
          d,
          lFwdURL,
          sFwdURL,
          lCallURL,
          sCallURL,
          lPutURL,
          sPutURL,
        ),
      )
      .attr("width", derivativeSize)
      .attr("height", derivativeSize);

    for (let i = 0; i < mainFields.length; i++) {
      // --- images highlight - alpha
      try {
        svg[mainFields[i]]
          .selectAll("image")
          .data(function(d) {
            return d;
          })
          .attr("xlink:href", (d) =>
            d.derivative_type.indexOf("OUT") > -1 && d.new_event
              ? mainAUrls[i]
              : mainUrls[i],
          );
        // eslint-disable-next-line no-empty
      } catch {}

      // --- line highlight - alpha
      try {
        svg[mainFields[i] + "Stick"].attr("stroke", (d) =>
          barrierColor(
            mainFields[i],
            d.derivative_type.indexOf("OUT") > -1 ? d.new_event : !d.new_event,
          ),
        );
        // eslint-disable-next-line no-empty
      } catch {}

      // --- rect highlight - alpha
      try {
        svg[mainFields[i] + "Rect"]
          .selectAll("rect")
          .data(function(d) {
            return d;
          })
          .attr("stroke", (d) =>
            barrierColor(
              mainFields[i],
              d.derivative_type.indexOf("OUT") > -1
                ? d.new_event
                : !d.new_event,
            ),
          )
          .style("fill", function(d) {
            return d.derivative_type.indexOf("OUT") > -1
              ? "rgba(0,0,0,0)"
              : barrierColor(
                  mainFields[i],
                  d.derivative_type.indexOf("OUT") > -1
                    ? d.new_event
                    : !d.new_event,
                );
          });
        // eslint-disable-next-line no-empty
      } catch {}
    }

    if (d.historicalIndex > -1) {
      select(".bandsTA" + d.historicalIndex).classed("hoved", false);
      select(".stickTA" + d.historicalIndex).classed("hoved", false);
      select(".stick2TA" + d.historicalIndex).classed("hoved", false);
      select(".candleTA" + d.historicalIndex).classed("hoved", false);
    }
  };

  // --- barriers
  let barrierData = data.filter((d) => isBarrier(d.derivative_type));
  if (barrierData.length > 0) {
    let widthRect = 15;
    // strike - barrier line
    let newData = dataForBarriers(barrierData, dateFld);
    var line = lineD3()
      .curve(curveLinear)
      .defined((d) => d.y)
      .x((d) => dateInterpolate(xRange, d[dateFld], svg.x, svg.x.bandwidth()))
      .y((d) => svg.y(d.y));

    svg[groupField + "Stick"] = svgAux
      .append("g")
      .attr("class", "sticks" + customName)
      .attr("fill", "none")
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .selectAll("path")
      .data(newData.series, (d) => d.id)
      .join("path")
      .style("stroke", barrierColor(groupField, true))
      .style("stroke-width", 1)
      .style("stroke-dasharray", dashArray(customName))
      .attr("d", (d) => line(d.values))
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");

    // barrier rectangle
    svg[groupField + "Rect"] = svgAux
      .selectAll(".barrierRect" + customName)
      .data([barrierData])
      .enter()
      .append("g")
      .attr("class", "barrierRect" + customName);
    svg[groupField + "Rect"]
      .selectAll("rect")
      .data(function(d) {
        return d;
      })
      .enter()
      .append("rect")
      .attr("x", function(d) {
        return (
          dateInterpolate(xRange, d[dateFld], svg.x, svg.x.bandwidth()) -
          widthRect / 2
        );
      })
      .attr("y", function(d) {
        return svg.y(d.barrier) - 2;
      })
      .attr("height", "2px")
      .attr("width", widthRect + "px")
      .attr("stroke-width", 1)
      .attr("stroke", (d) =>
        barrierColor(
          groupField,
          d.derivative_type.indexOf("OUT") > -1 ? d.new_event : !d.new_event,
        ),
      )
      .style("fill", function(d) {
        return d.derivative_type.indexOf("OUT") > -1
          ? "rgba(0,0,0,0)"
          : barrierColor(
              groupField,
              d.derivative_type.indexOf("OUT") > -1
                ? d.new_event
                : !d.new_event,
            );
      });
  }
  const svgBodyImages = isAnalytics ? svgAux : svg;
  // --- derivative images
  svg[groupField] = svgBodyImages
    .selectAll(".scatter" + customName)
    .data([data])
    .enter()
    .append("g")
    .attr("class", ".scatter" + customName);
  svg[groupField]
    .selectAll("image")
    .data(function(d) {
      return d;
    })
    .enter()
    .append("image")
    .attr("xlink:href", (d) =>
      d.derivative_type.indexOf("OUT") > -1 && d.new_event ? imgURLOff : imgURL,
    )
    .attr("x", function(d) {
      return (
        dateInterpolate(xRange, d[dateFld], svg.x, svg.x.bandwidth()) -
        derivativeSize / 2
      );
    })
    .attr("y", function(d) {
      return svg.y(d.strike) - derivativeSize / 2;
    })
    .attr("width", derivativeSize)
    .attr("height", derivativeSize)
    .on("mouseover", mousemove)
    .on("mouseleave", mouseleave)
    .on("mousemove", mousemove);
};

export function isBarrier(derivative_type) {
  return (
    derivative_type === "UP AND IN" ||
    derivative_type === "UP AND OUT" ||
    derivative_type === "DOWN AND IN" ||
    derivative_type === "DOWN AND OUT"
  );
}

export function dashArray(name) {
  return name === "LFWD"
    ? "4, 4"
    : name === "SFWD"
    ? "4, 4"
    : name === "LCALL"
    ? "4, 2"
    : name === "SCALL"
    ? "4, 2"
    : name === "LPUT"
    ? "2, 4"
    : "2, 4";
}

export function barrierColor(name, alpha) {
  return name === "longFwds" && !alpha
    ? colorFwd
    : name === "longFwds" && alpha
    ? colorFwdAlpha
    : name === "shortFwds" && !alpha
    ? darkFwd
    : name === "shortFwds" && alpha
    ? darkFwdAlpha
    : name === "longCalls" && !alpha
    ? colorCall
    : name === "longCalls" && alpha
    ? colorCallAlpha
    : name === "shortCalls" && !alpha
    ? darkCall
    : name === "shortCalls" && alpha
    ? darkCallAlpha
    : name === "longPuts" && !alpha
    ? colorPut
    : name === "longPuts" && alpha
    ? colorPutAlpha
    : name === "shortPuts" && !alpha
    ? darkPut
    : darkPutAlpha;
}

export function dataForBarriers(data, dateFld = "date") {
  let series1 = data.map(function(d) {
    return {
      id: +d.date,
      alpha: d.new_event,
      strategy_code: d.strategy_code,
      values: [
        { [dateFld]: d[dateFld], y: Math.max(d.strike, d.barrier) },
      ].concat([{ [dateFld]: d[dateFld], y: Math.min(d.strike, d.barrier) }]),
    };
  });
  return {
    name: "barriers",
    series: series1,
  };
}

export function cleanAndUnionName(name) {
  if (name) {
    return name
      .replace(/ /g, "_")
      .replace(/[.]|\d+/g, function(match) {
        return match === "." ? "_" : "num" + match;
      })
      .replace(/&/g, "_");
  }
}
